import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { tvSeriesList, categories } from '@/mocks/tvSeries';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const series = useMemo(
    () => tvSeriesList.find((s) => s.id === Number(id)),
    [id]
  );

  const categoryName = useMemo(() => {
    if (!series) return '';
    const cat = categories.find((c) => c.id === series.categoryId);
    return cat?.name || '';
  }, [series]);

  const relatedSeries = useMemo(() => {
    if (!series) return [];
    return tvSeriesList
      .filter((s) => s.categoryId === series.categoryId && s.id !== series.id)
      .slice(0, 6);
  }, [series]);

  if (!series) {
    return (
      <>
        <Navbar />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-3">
              剧集未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-8">您要查看的电视剧不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('article.backToList')}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: categoryName, to: `/category/${series.categoryId}` },
    { label: series.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${series.title} - 四虎影视大全电视剧全集在线观看`,
    description: series.description,
    image: series.coverImage,
    datePublished: series.releaseDate,
    author: {
      '@type': 'Organization',
      name: '四虎影视大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '四虎影视大全',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.sihutv.com/article/${series.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.sihutv.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `https://www.sihutv.com/category/${series.categoryId}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: series.title,
        item: `https://www.sihutv.com/article/${series.id}`,
      },
    ],
  };

  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <div className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <main>
          {/* Banner */}
          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={series.bannerImage}
              alt={`${series.title} - 四虎影视大全`}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
          </section>

          {/* Content */}
          <section className="px-4 md:px-6 -mt-32 relative z-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Left - Cover */}
                <div className="flex-shrink-0 w-full sm:w-56 lg:w-64">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-lg">
                    <img
                      src={series.coverImage}
                      alt={`${series.title} 电视剧海报`}
                      title={`${series.title} - 四虎影视大全电视剧全集`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Right - Info */}
                <div className="flex-1 pt-0 lg:pt-20">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                    {series.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="flex items-center gap-1 text-sm text-background-50/80">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                      </div>
                      <strong className="text-background-50">{series.rating}</strong>
                    </span>
                    <span className="text-background-50/40">|</span>
                    <span className="text-sm text-background-50/80">{series.year}</span>
                    <span className="text-background-50/40">|</span>
                    <span className="text-sm text-background-50/80">{series.region}</span>
                    <span className="text-background-50/40">|</span>
                    <span className="text-sm text-background-50/80">
                      {series.totalEpisodes}{t('category.episodes')}
                    </span>
                    <span className="text-background-50/40">|</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-accent-500 text-background-50">
                      {series.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {series.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1 rounded-full bg-background-50/10 text-background-50/80 border border-background-50/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-background-50/70 leading-relaxed max-w-2xl line-clamp-3">
                    {series.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Details Section */}
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2">
                  {/* Full Description */}
                  <div className="mb-10">
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                      {t('article.description')}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {series.fullDescription}
                    </p>
                  </div>

                  {/* Episodes */}
                  <div>
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                      {t('article.episodeList')}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {series.episodes.map((ep) => (
                        <div
                          key={ep.id}
                          className="flex items-center justify-between p-3.5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50">
                              <span className="text-xs font-bold text-primary-500">
                                {ep.id}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-foreground-800">
                              {ep.title}
                            </span>
                          </div>
                          <span className="text-xs text-foreground-500 whitespace-nowrap">
                            {ep.duration}
                          </span>
                        </div>
                      ))}
                    </div>
                    {series.totalEpisodes > series.episodes.length && (
                      <div className="mt-4 text-center">
                        <button className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                          展开全部 {series.totalEpisodes} 集
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="sticky top-24">
                    <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                      <h3 className="text-sm font-heading font-bold text-foreground-900 mb-4">
                        基本信息
                      </h3>
                      <dl className="space-y-3">
                        <InfoRow label={t('article.director')} value={series.director} />
                        <InfoRow label={t('article.actors')} value={series.actors.join(' / ')} />
                        <InfoRow label={t('article.region')} value={series.region} />
                        <InfoRow label={t('article.language')} value={series.language} />
                        <InfoRow
                          label={t('article.episodes')}
                          value={`${series.totalEpisodes} ${t('category.episodes')}`}
                        />
                        <InfoRow label={t('article.status')} value={series.status} />
                        <InfoRow label={t('article.rating')} value={`${series.rating} 分`} />
                        <InfoRow label={t('article.year')} value={String(series.year)} />
                      </dl>
                    </div>

                    {/* Related */}
                    {relatedSeries.length > 0 && (
                      <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200/70">
                        <h3 className="text-sm font-heading font-bold text-foreground-900 mb-4">
                          相关推荐
                        </h3>
                        <div className="space-y-3">
                          {relatedSeries.slice(0, 4).map((rel) => (
                            <Link
                              key={rel.id}
                              to={`/article/${rel.id}`}
                              className="flex items-center gap-3 group cursor-pointer"
                            >
                              <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden">
                                <img
                                  src={rel.coverImage}
                                  alt={rel.title}
                                  title={rel.title}
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-500 transition-colors">
                                  {rel.title}
                                </p>
                                <p className="text-xs text-foreground-500 mt-0.5">
                                  {rel.rating} 分 · {rel.totalEpisodes}集
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Related Series Grid */}
          {relatedSeries.length > 0 && (
            <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedSeries.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">
                          {rel.rating} 分
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
      <Footer />
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <dt className="text-xs text-foreground-500 whitespace-nowrap min-w-[3rem]">{label}</dt>
      <dd className="text-xs text-foreground-800">{value}</dd>
    </div>
  );
}